(ns ncr.etc.util
  (:import goog.async.Delay)
  (:require
   [cljs.core.async :as async]
   [clojure.string :as str]
   [goog.string :as gstring]
   [goog.userAgent :as guserAgent]
   [goog.math.Integer :as int]
   [goog.math :refer [Rect Coordinate]]
   [goog.crypt :as gcrypt]
   [clojure.core.match :refer [match]]
   [taoensso.encore :as e]
   [tick.core :as t]
   [tick.locale-en-us]
   [cljc.java-time.local-date]
   [ncr.transit :as transit]
   [shadow.cljs.modern :refer [js-await]]
   [uix.core.alpha :as uix]
   ["react-dom/server" :as react-dom-server]
   ["bignumber.js" :as big]
   ["date-fns" :as dfns]
   [cemerick.url :as url])
  (:import
   [goog.crypt Md5])

  (:require-macros
   [ncr.etc.util :refer
    [error-m if-ok when-ok without]]))

(defn md5 [in]
  (let [m (Md5.)]
    (.reset m)
    (.update m in)
    (gcrypt/byteArrayToHex
      (.digest m))))

(def format
  gstring/format)

(def local-date-time-formatter
  (t/formatter "MMM d, HH:mm"))

(defn format-local-date-time [in]
  (->> (e/have! string? in)
    (t/date-time)
    (t/format local-date-time-formatter)))

(defn date-distance [date]
  (dfns/formatDistanceToNow (js/Date. date) #js {:addSuffix true}))

(defn parse-number [s]
  (when s
    (.toNumber (int/fromString s))))

(defn json-decode [in]
  (if (string? in)
    (-> in js/JSON.parse (js->clj :keywordize-keys true))
    in))

(defn m-fail? [x]
  (match x [:error _] true _ false))

(defn m-failed [x]
  (when (m-fail? x) x))

(defn m-result [tag x]
  (match x [tag v] v _ x))

(defn index-by [ifn coll]
  (reduce
    (fn [acc value]
      (assoc acc (ifn value) value)) {} coll))

(defn remove-nth [n seq]
  (->> seq
    (map-indexed
      (fn [index item]
        (if (not= index n) item ::marker)))
    (filter
      #(not= % ::marker))))

(defn split-file-name [in]
  (when in
    (let [[_ base ext] (re-find #"^(.*?)(\.[^.]*)?$" in)]
      [base ext])))

(defn base-file-name [in]
  (when in
    (let [[_ base _] (re-find #"(.*?)(\.\w+)?$" in)]
      base)))

(defn http-error [{:keys [success body] :as result}]
  (if success
    [:ok body]
    [:error
     (let [{:keys [status error-code error-text]} result]
       {:status status :error-code error-code :error-text error-text})]))

(defn open-link!
  ([url]
   (open-link! url :INLINE))
  ([url disposition]
   (let [link (.createElement js/document "a")]
     (set! (.-href link) url)
     (when (= disposition :INLINE)
       (set! (.-target link) "_blank"))
     (.click link))))

(def is-development-environment?
  (let [href (-> js/window .-location .-href)]
    (boolean
      (some
        #(str/starts-with? href %) ["http://local"]))))

(defn set-toggle [s v]
  ((if (s v) disj conj) s v))

(defn with-ctrl? [e]
  (or
    (and (or guserAgent/WINDOWS guserAgent/LINUX) (.-ctrlKey e))
    (and guserAgent/MAC (.-metaKey e))))

(defn with-shift? [e]
  (.-shiftKey e))

(defn with-alt? [e]
  (.-altKey e))

(defn on-click-modifiers
  ([hmap]
   (fn [e]
     (on-click-modifiers e hmap)))
  ([e {:keys [on-ctrl-click on-alt-click on-shift-click on-click]}]
   (cond
     (and (fn? on-ctrl-click) (with-ctrl? e)) (on-ctrl-click e)
     (and (fn? on-alt-click) (with-alt? e)) (on-alt-click e)
     (and (fn? on-shift-click) (with-shift? e)) (on-shift-click e)
     (fn? on-click) (on-click e)
     :else nil)))

(defn stop-propagation [e]
  (.stopPropagation e))

(defn isolate*
  ([pred h]
   (fn [& [^js e & _ :as all]]
     (when (pred e)
       (.stopPropagation e)
       (.preventDefault e)
       (when h
         (apply h all)))))
  ([h]
   (isolate* (constantly true) h)))

(defn isolate [h]
  (fn [& [^js e & _ :as all]]
    (when (apply h all)
      (.stopPropagation e)
      (.preventDefault e))))

(defn or*
  ([]
   nil)
  ([a]
   a)
  ([a b]
   (or a b))
  ([a b c]
   (or a b c))
  ([a b c d]
   (or a b c d))
  ([a b c d & rest]
   (or a b c d
     (reduce or* rest))))

(defn and*
  ([]
   true)
  ([a]
   a)
  ([a b]
   (and a b))
  ([a b c]
   (and a b c))
  ([a b c d]
   (and a b c d))
  ([a b c d & rest]
   (and a b c d
     (reduce and* rest))))

(defn singleton [c]
  (when (and (seq c) (empty? (rest c)))
    (first c)))

(defn distinct-singleton [c]
  (-> c distinct singleton))

(defn bignumber [s]
  (when-not (str/blank? s)
    (big/BigNumber. s)))

(defn transit-read [s]
  (try
    (transit/decode s)
    (catch js/Error e
      [:error :etransit e])))

(defn transit-write [v]
  (transit/encode v))

(defn email? [s]
  (boolean
    (and
      (string? s)
      (re-seq #"^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$" s))))

(defn initials [s]
  (->> (str/split (str/trim s) #"\s+")
    (take 2)
    (map first)
    (apply str)
    (str/upper-case)))

(defn set-timeout [timeout callback-fn]
  (let [delay (Delay. callback-fn timeout)]
    (.start delay)
    delay))

(defn key-code [event]
  (let [key-code (.-keyCode event)]
    (if (= 0 key-code)
      (.-which event)
      key-code)))

(defn set-page-title
  ([] (set-page-title "" ""))
  ([text cluster-name]
   (set! (.-title js/document)
     (str
       (when (seq text) (str text " | "))
       (when (seq cluster-name) (str cluster-name "."))
       "neckar.io"))))

; based on https://stackoverflow.com/a/68677985/1016019
; may not work with React 17+ (based on fibers)
(defn set-input-value [^js input value]
  (let [event (js/Event. "change" #js {:bubbles true})
        value-tracker (.-_valueTracker input)
        old-value (.-value input)]
    (set! (.-value input) value)
    (.setValue value-tracker old-value)
    (.dispatchEvent input event)))

(defn query-string-param [param]
  (get-in
    (-> js/window .-location .-href url/url) [:query param]))

(defn update-when [m k f & more]
  (if (contains? m k)
    (apply update m k f more)
    m))

(defn- fit-axis [t s c cs]
  (if (< t c) c (let [cr (+ c cs)] (if (> (+ t s) cr) (- cr s) t))))
(defn- fit-size [^js t ^js c]
  (if (.fitsInside t c) t (.scaleToFit t c)))

(defn fit-rect
  "Adjust `target` Rect preserving the aspect ratio to fit inside `container` rect."
  [^js target ^js container]
  (if (.contains container target)
    target
    (let [^js s (fit-size (.getSize target) (.getSize container))]
      (.createFromPositionAndSize ^js Rect
        (Coordinate.
          (fit-axis (.-left target) (.-width s) (.-left container) (.-width container))
          (fit-axis (.-top target) (.-height s) (.-top container) (.-height container)))
        s))))

(defn dedupe-with-fn
  "Returns a lazy sequence replacing duplicates with result of (f [item idx]).
  Returns a transducer when no collection is provided."
  ([f]
   (fn [rf]
     (let [pa (volatile! {})]
       (fn
         ([] (rf))
         ([result] (rf result))
         ([result input]
          (rf result
            (let [idx (get @pa input)
                  new-input (if idx (f input idx) input)]
              (vswap! pa assoc input (inc idx) new-input 1)
              new-input)))))))
  ([f coll] (sequence (dedupe-with-fn f) coll)))

(defn- name-or-str
  ([prefix s]
   (cond
     (nil? s) nil
     (string? s) (str prefix s)
     (keyword? s) (name-or-str prefix (name s))
     (coll? s) (name-or-str prefix (when (seq s) (str/join "-" (map name-or-str s))))
     :else
     s))
  ([s]
   (name-or-str nil s)))

(defn path->key [path]
  (cond
    (string? path) path
    (map? path) (let [{:keys [report-path cube-path sym-path uniq]} path]
                  (str (name-or-str report-path) (name-or-str "_" cube-path) (name-or-str "_" sym-path)
                    (name-or-str "__" uniq)))
    :else nil))

(defn assoc-when
  ([coll k v]
   (if v (assoc coll k v) coll))
  ([coll k v & kvs]
   (let [ret (assoc-when coll k v)]
     (if kvs
       (recur ret (first kvs) (second kvs) (nnext kvs))
       ret))))

(defn assoc-or-dissoc
  ([coll k v]
   (if v (assoc coll k v) (dissoc coll k)))
  ([coll k v & kvs]
   (let [ret (assoc-or-dissoc coll k v)]
     (if kvs
       (recur ret (first kvs) (second kvs) (nnext kvs))
       ret))))

(defn promise->ch
  "returns async channel from promise function"
  [promise-fn]
  (let [ch (async/promise-chan)]
    (js-await [result (promise-fn)]
      (async/put! ch result)
      (catch error (async/put! ch [:error error])))
    ch))

